import { useRef, useState, useCallback, useEffect } from "react";
import {
    GetCourseDetails,
    GetLearningUrl,
    LearningCourse
} from "../../handler/coursehandler";

const packageJson = require("../../../package.json");

interface InnerBannerProps {
    courseId: number;
}

interface CourseVideo {
    courseVideoId: number;
    courseVideoName: string;
    courseServerName: string;
    totalVideoDurationInSeconds: number;
    currentVideoDurationInSeconds: number;
    courseId: number;
    videoPercentage: number;
}

interface CourseLearningData {
    courseId: number;
    courseName: string;
    serverName: string;
    courseDescription: string;
    packageId: number;
    coursePrice: number;
    percentage: number;
    currentVideoId: number;
    courseVideos: CourseVideo[];
}

const Learning: React.FC<InnerBannerProps> = ({ courseId }) => {

    /*
     * =========================================================
     * VIDEO
     * =========================================================
     */

    const [videoShow, setVideoShow] = useState("");

    const playerRef =
        useRef<HTMLVideoElement | null>(null);


    /*
     * =========================================================
     * VIDEO PLAY STATE
     * =========================================================
     */

    const [isVideoPlay, setIsVideoPlay] =
        useState(false);


    /*
     * =========================================================
     * USER
     * =========================================================
     */

    const [userInformationId, setUserInformationId] =
        useState(0);


    /*
     * =========================================================
     * SELECTED VIDEO
     * =========================================================
     */

    const [selectedCourse, setSelectedCourse] =
        useState<CourseVideo>({
            courseVideoId: 0,
            courseVideoName: "",
            courseServerName: "",
            totalVideoDurationInSeconds: 0,
            currentVideoDurationInSeconds: 0,
            courseId: 0,
            videoPercentage: 0
        });


    /*
     * =========================================================
     * COURSE
     * =========================================================
     */

    const [courseLearning, setCourseLearning] =
        useState<CourseLearningData>({
            courseId: 0,
            courseName: "",
            serverName: "",
            courseDescription: "",
            packageId: 0,
            coursePrice: 0,
            percentage: 0,
            currentVideoId: 0,
            courseVideos: []
        });


    /*
     * =========================================================
     * PREVENT MULTIPLE API CALLS
     * =========================================================
     */

    const lastSavedSecond =
        useRef<number>(-1);

    const loadingCourse =
        useRef(false);


    /*
     * =========================================================
     * GET VIDEO URL
     * =========================================================
     */

    const learningUrl = async (
        serverName: string,
        courseServerName: string
    ) => {

        try {

            const response =
                await GetLearningUrl({
                    courseName: serverName,
                    courseServerName:
                        courseServerName
                });


            if (response?.statusCode === 200) {

                const videoUrl =
                    packageJson.videoApiUrl +
                    serverName +
                    "/" +
                    courseServerName +
                    ".mp4?" +
                    packageJson.awsAccessKeyId +
                    response.data.videoPrivateKey;


                setVideoShow(videoUrl);

            }

        } catch (error) {

            console.error(
                "Error getting learning URL:",
                error
            );

        }
    };


    /*
     * =========================================================
     * SAVE VIDEO PROGRESS
     * =========================================================
     */

    const saveVideoProgress = async () => {

        if (
            !userInformationId ||
            !selectedCourse.courseVideoId ||
            !playerRef.current
        ) {
            return;
        }


        try {

            await LearningCourse({
                userInformationId:
                    userInformationId,

                courseId:
                    courseId,

                courseVideoId:
                    selectedCourse.courseVideoId,

                videoTiming:
                    playerRef.current.currentTime
            });

        } catch (error) {

            console.error(
                "Error saving video progress:",
                error
            );

        }
    };


    /*
     * =========================================================
     * GET COURSE DETAILS
     * =========================================================
     */

    const getCourseDetails = async (
        currentUserInformationId: number
    ) => {

        if (loadingCourse.current) {
            return;
        }

        loadingCourse.current = true;

        try {

            const response =
                await GetCourseDetails(courseId);


            if (
                response?.statusCode !== 200 ||
                !response?.data?.courses
            ) {
                return;
            }


            const course =
                response.data.courses;


            setCourseLearning(course);


            if (
                !course.courseVideos ||
                course.courseVideos.length === 0
            ) {
                return;
            }


            /*
             * -------------------------------------------------
             * FIND CURRENT VIDEO
             * -------------------------------------------------
             */

            let currentRunningCourseVideo:
                CourseVideo | undefined;


            if (course.currentVideoId > 0) {

                currentRunningCourseVideo =
                    course.courseVideos.find(
                        (video: CourseVideo) =>
                            video.courseVideoId ===
                            course.currentVideoId
                    );

            }


            /*
             * If no current video exists,
             * start from first video.
             */

            if (!currentRunningCourseVideo) {

                currentRunningCourseVideo =
                    course.courseVideos[0];

            }


            if (!currentRunningCourseVideo) {
                return;
            }


            setSelectedCourse(
                currentRunningCourseVideo
            );


            /*
             * Reset save tracker for new video.
             */

            lastSavedSecond.current = -1;


            /*
             * Get video URL.
             */

            await learningUrl(
                course.serverName,
                currentRunningCourseVideo.courseServerName
            );


            /*
             * Save current video information.
             */

            await LearningCourse({
                userInformationId:
                    currentUserInformationId,

                courseId:
                    courseId,

                courseVideoId:
                    currentRunningCourseVideo.courseVideoId,

                videoTiming:
                    currentRunningCourseVideo
                        .currentVideoDurationInSeconds
            });


        } catch (error) {

            console.error(
                "Error loading course details:",
                error
            );

        } finally {

            loadingCourse.current = false;

        }
    };


    /*
     * =========================================================
     * LOAD USER + COURSE
     * =========================================================
     */

    const onReady = useCallback(
        async (
            currentUserInformationId: number
        ) => {

            await getCourseDetails(
                currentUserInformationId
            );

        },
        [courseId]
    );


    useEffect(() => {

        try {

            const storedProfile =
                localStorage.getItem(
                    "userProfile"
                );


            if (!storedProfile) {

                console.error(
                    "userProfile not found"
                );

                return;

            }


            const userProfile =
                JSON.parse(
                    storedProfile
                );


            const currentUserInformationId =
                Number(
                    userProfile?.userInformationId ??
                    0
                );


            if (!currentUserInformationId) {

                console.error(
                    "Invalid userInformationId"
                );

                return;

            }


            setUserInformationId(
                currentUserInformationId
            );


            window.scrollTo(
                0,
                0
            );


            onReady(
                currentUserInformationId
            );

        } catch (error) {

            console.error(
                "Error loading user profile:",
                error
            );

        }

    }, [onReady]);


    /*
     * =========================================================
     * SET VIDEO POSITION AFTER VIDEO LOADS
     * =========================================================
     */

    useEffect(() => {

        const video =
            playerRef.current;


        if (!video) {
            return;
        }


        const handleLoadedMetadata =
            () => {

                const startTime =
                    Number(
                        selectedCourse
                            .currentVideoDurationInSeconds ??
                        0
                    );


                if (
                    startTime > 0 &&
                    startTime < video.duration
                ) {

                    video.currentTime =
                        startTime;

                }

            };


        video.addEventListener(
            "loadedmetadata",
            handleLoadedMetadata
        );


        return () => {

            video.removeEventListener(
                "loadedmetadata",
                handleLoadedMetadata
            );

        };

    }, [
        videoShow,
        selectedCourse.courseVideoId,
        selectedCourse.currentVideoDurationInSeconds
    ]);


    /*
     * =========================================================
     * UPDATE COURSE DETAILS AFTER SAVING
     * =========================================================
     */

    const getCourseDetailsOnUpdate =
        async () => {

            try {

                const response =
                    await GetCourseDetails(
                        courseId
                    );


                if (
                    response?.statusCode !== 200 ||
                    !response?.data?.courses
                ) {
                    return;
                }


                const course =
                    response.data.courses;


                setCourseLearning(
                    course
                );


                /*
                 * Keep currently selected video.
                 */

                const currentVideo =
                    course.courseVideos?.find(
                        (video: CourseVideo) =>
                            video.courseVideoId ===
                            selectedCourse.courseVideoId
                    );


                if (currentVideo) {

                    setSelectedCourse(
                        currentVideo
                    );

                }

            } catch (error) {

                console.error(
                    "Error refreshing course:",
                    error
                );

            }

        };


    /*
     * =========================================================
     * UPDATE LEARNING
     * =========================================================
     */

    const UpdateLearningCourse =
        async () => {

            await saveVideoProgress();

            await getCourseDetailsOnUpdate();

        };


    /*
     * =========================================================
     * CHANGE VIDEO
     * =========================================================
     */

    const changeVideo =
        async (courseVideo: CourseVideo) => {

            /*
             * Same video = Play / Pause
             */

            if (
                selectedCourse.courseVideoId ===
                courseVideo.courseVideoId
            ) {

                if (!playerRef.current) {
                    return;
                }


                if (
                    playerRef.current.paused
                ) {

                    try {

                        await playerRef.current.play();

                        setIsVideoPlay(true);

                    } catch (error) {

                        console.error(
                            "Unable to play video:",
                            error
                        );

                    }

                } else {

                    playerRef.current.pause();

                    setIsVideoPlay(false);

                }

                return;
            }


            /*
             * -------------------------------------------------
             * NEW VIDEO
             * -------------------------------------------------
             */

            setIsVideoPlay(false);

            setSelectedCourse(
                courseVideo
            );


            lastSavedSecond.current = -1;


            try {

                /*
                 * Save selected video to backend.
                 */

                await LearningCourse({
                    userInformationId:
                        userInformationId,

                    courseId:
                        courseId,

                    courseVideoId:
                        courseVideo.courseVideoId,

                    videoTiming:
                        courseVideo
                            .currentVideoDurationInSeconds
                });


                /*
                 * Get new video URL.
                 */

                await learningUrl(
                    courseLearning.serverName,
                    courseVideo.courseServerName
                );

            } catch (error) {

                console.error(
                    "Error changing video:",
                    error
                );

            }

        };


    /*
     * =========================================================
     * VIDEO TIME UPDATE
     * =========================================================
     */

    const handleTimeUpdate = () => {

        const video =
            playerRef.current;


        if (!video) {
            return;
        }


        const currentTime =
            Math.floor(
                video.currentTime
            );


        const totalTime =
            Math.floor(
                video.duration || 0
            );


        /*
         * -------------------------------------------------
         * VIDEO COMPLETED
         * -------------------------------------------------
         */

        if (
            totalTime > 0 &&
            currentTime >= totalTime - 1
        ) {

            if (
                lastSavedSecond.current !==
                -2
            ) {

                lastSavedSecond.current =
                    -2;

                UpdateLearningCourse();

            }

            return;
        }


        /*
         * -------------------------------------------------
         * SAVE EVERY 60 SECONDS
         * -------------------------------------------------
         */

        if (
            currentTime > 0 &&
            currentTime % 60 === 0 &&
            lastSavedSecond.current !==
                currentTime
        ) {

            lastSavedSecond.current =
                currentTime;

            UpdateLearningCourse();

        }

    };


    /*
     * =========================================================
     * VIDEO PLAY
     * =========================================================
     */

    const handlePlay = () => {

        setIsVideoPlay(true);

    };


    /*
     * =========================================================
     * VIDEO PAUSE
     * =========================================================
     */

    const handlePause = () => {

        setIsVideoPlay(false);

    };


    /*
     * =========================================================
     * VIDEO ENDED
     * =========================================================
     */

    const handleEnded = () => {

        setIsVideoPlay(false);

        UpdateLearningCourse();

    };


    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (

        <section
            style={{
                width: "100%",
                minHeight: "100vh",
                background: "#050505",
                color: "#fff"
            }}
        >

            <div
                style={{
                    width: "100%",
                    background:
                        "radial-gradient(circle at top, #161616 0%, #050505 55%, #000 100%)",
                    padding:
                        "25px 15px 40px",
                    boxSizing:
                        "border-box"
                }}
            >

                <div
                    style={{
                        width: "100%",
                        maxWidth: "1400px",
                        margin: "0 auto"
                    }}
                >

                    {/* =================================================
                        COURSE HEADER
                    ================================================== */}

                    <div
                        style={{
                            marginBottom:
                                "20px"
                        }}
                    >

                        <h2
                            style={{
                                margin: 0,
                                fontSize:
                                    "26px",
                                fontWeight:
                                    700,
                                color:
                                    "#fff"
                            }}
                        >
                            {courseLearning.courseName ||
                                "Course Learning"}
                        </h2>


                        {courseLearning.courseDescription && (

                            <p
                                style={{
                                    margin:
                                        "7px 0 0",
                                    color:
                                        "#999",
                                    fontSize:
                                        "14px"
                                }}
                            >
                                {
                                    courseLearning
                                        .courseDescription
                                }
                            </p>

                        )}

                    </div>


                    {/* =================================================
                        VIDEO
                    ================================================== */}

                    <div
                        style={{
                            width: "100%",
                            background:
                                "#000",
                            borderRadius:
                                "16px",
                            overflow:
                                "hidden",
                            border:
                                "1px solid #222",
                            boxShadow:
                                "0 15px 40px rgba(0,0,0,0.5)"
                        }}
                    >

                        <video
                            key={videoShow}
                            ref={playerRef}
                            controls
                            controlsList="nodownload"
                            preload="metadata"

                            /*
                             * Do not allow right click.
                             */

                            onContextMenu={(
                                e
                            ) =>
                                e.preventDefault()
                            }

                            onTimeUpdate={
                                handleTimeUpdate
                            }

                            onPlay={
                                handlePlay
                            }

                            onPause={
                                handlePause
                            }

                            onEnded={
                                handleEnded
                            }

                            className="videoSelect"

                            style={{
                                width:
                                    "100%",
                                maxHeight:
                                    "75vh",
                                display:
                                    "block",
                                background:
                                    "#000"
                            }}
                        >

                            {videoShow && (

                                <source
                                    src={
                                        videoShow
                                    }
                                    type="video/mp4"
                                />

                            )}

                            Your browser does not
                            support the video tag.

                        </video>


                        {/* =================================================
                            VIDEO INFORMATION
                        ================================================== */}

                        <div
                            style={{
                                padding:
                                    "18px 20px",
                                background:
                                    "#0c0c0c"
                            }}
                        >

                            <h3
                                style={{
                                    margin:
                                        "0 0 12px",
                                    fontSize:
                                        "19px",
                                    color:
                                        "#fff"
                                }}
                            >
                                {
                                    selectedCourse
                                        .courseVideoName ||
                                    "Video Title"
                                }
                            </h3>


                            <div
                                style={{
                                    display:
                                        "flex",
                                    gap:
                                        "25px",
                                    flexWrap:
                                        "wrap",
                                    color:
                                        "#aaa",
                                    fontSize:
                                        "13px"
                                }}
                            >

                                <span>
                                    <strong
                                        style={{
                                            color:
                                                "#fff"
                                        }}
                                    >
                                        Total Course:
                                    </strong>{" "}
                                    {
                                        courseLearning
                                            .percentage ||
                                        0
                                    }%
                                </span>


                                <span>
                                    <strong
                                        style={{
                                            color:
                                                "#fff"
                                        }}
                                    >
                                        Chapter:
                                    </strong>{" "}
                                    {
                                        selectedCourse
                                            .videoPercentage ||
                                        0
                                    }%
                                </span>


                                <span>
                                    <strong
                                        style={{
                                            color:
                                                "#fff"
                                        }}
                                    >
                                        Videos:
                                    </strong>{" "}
                                    {
                                        courseLearning
                                            .courseVideos
                                            .length
                                    }
                                </span>

                            </div>

                        </div>

                    </div>


                    {/* =================================================
                        CHAPTER LIST
                    ================================================== */}

                    <div
                        style={{
                            marginTop:
                                "25px"
                        }}
                    >

                        <div
                            style={{
                                display:
                                    "flex",
                                alignItems:
                                    "center",
                                justifyContent:
                                    "space-between",
                                marginBottom:
                                    "12px"
                            }}
                        >

                            <h3
                                style={{
                                    margin: 0,
                                    fontSize:
                                        "18px",
                                    color:
                                        "#fff"
                                }}
                            >
                                Course Chapters
                            </h3>


                            <span
                                style={{
                                    fontSize:
                                        "12px",
                                    color:
                                        "#888"
                                }}
                            >
                                {
                                    courseLearning
                                        .courseVideos
                                        .length
                                }{" "}
                                Chapters
                            </span>

                        </div>


                        <div
                            style={{
                                maxHeight:
                                    "400px",
                                overflowY:
                                    "auto",
                                background:
                                    "#090909",
                                border:
                                    "1px solid #202020",
                                borderRadius:
                                    "14px",
                                padding:
                                    "8px"
                            }}
                        >

                            {courseLearning
                                .courseVideos
                                .map(
                                    (
                                        course,
                                        index
                                    ) => {

                                        const isSelected =
                                            selectedCourse
                                                .courseVideoId ===
                                            course.courseVideoId;


                                        return (

                                            <div
                                                key={
                                                    course.courseVideoId
                                                }

                                                onClick={() =>
                                                    changeVideo(
                                                        course
                                                    )
                                                }

                                                style={{
                                                    padding:
                                                        "15px",

                                                    marginBottom:
                                                        index ===
                                                        courseLearning
                                                            .courseVideos
                                                            .length -
                                                            1
                                                            ? 0
                                                            : "7px",

                                                    cursor:
                                                        "pointer",

                                                    background:
                                                        isSelected
                                                            ? "#1d1d1d"
                                                            : "#101010",

                                                    border:
                                                        isSelected
                                                            ? "1px solid #f5b45f"
                                                            : "1px solid #1c1c1c",

                                                    borderRadius:
                                                        "10px",

                                                    transition:
                                                        "all 0.2s ease"
                                                }}
                                            >

                                                <div
                                                    style={{
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        gap:
                                                            "13px"
                                                    }}
                                                >

                                                    {/* NUMBER */}

                                                    <div
                                                        style={{
                                                            width:
                                                                "34px",
                                                            height:
                                                                "34px",
                                                            minWidth:
                                                                "34px",
                                                            borderRadius:
                                                                "50%",
                                                            display:
                                                                "flex",
                                                            alignItems:
                                                                "center",
                                                            justifyContent:
                                                                "center",

                                                            background:
                                                                isSelected
                                                                    ? "#f5b45f"
                                                                    : "#222",

                                                            color:
                                                                isSelected
                                                                    ? "#000"
                                                                    : "#aaa",

                                                            fontSize:
                                                                "13px",
                                                            fontWeight:
                                                                700
                                                        }}
                                                    >
                                                        {
                                                            index +
                                                            1
                                                        }
                                                    </div>


                                                    {/* NAME */}

                                                    <div
                                                        style={{
                                                            flex:
                                                                1,
                                                            minWidth:
                                                                0
                                                        }}
                                                    >

                                                        <div
                                                            style={{
                                                                color:
                                                                    isSelected
                                                                        ? "#fff"
                                                                        : "#ccc",

                                                                fontSize:
                                                                    "14px",

                                                                fontWeight:
                                                                    isSelected
                                                                        ? 700
                                                                        : 500,

                                                                overflow:
                                                                    "hidden",

                                                                textOverflow:
                                                                    "ellipsis",

                                                                whiteSpace:
                                                                    "nowrap"
                                                            }}
                                                        >
                                                            {
                                                                course.courseVideoName
                                                            }
                                                        </div>


                                                        <div
                                                            style={{
                                                                marginTop:
                                                                    "5px",
                                                                fontSize:
                                                                    "11px",
                                                                color:
                                                                    "#777"
                                                            }}
                                                        >
                                                            Progress:{" "}
                                                            {
                                                                course.videoPercentage ||
                                                                0
                                                            }%
                                                        </div>

                                                    </div>


                                                    {/* PLAY / PAUSE */}

                                                    <div
                                                        style={{
                                                            width:
                                                                "35px",
                                                            textAlign:
                                                                "center",
                                                            fontSize:
                                                                "17px"
                                                        }}
                                                    >

                                                        {isSelected
                                                            ? (
                                                                isVideoPlay
                                                                    ? "⏸"
                                                                    : "▶"
                                                            )
                                                            : "▶"}

                                                    </div>

                                                </div>


                                                {/* CHAPTER PROGRESS */}

                                                <div
                                                    style={{
                                                        width:
                                                            "100%",
                                                        height:
                                                            "3px",
                                                        background:
                                                            "#242424",
                                                        borderRadius:
                                                            "10px",
                                                        marginTop:
                                                            "12px",
                                                        overflow:
                                                            "hidden"
                                                    }}
                                                >

                                                    <div
                                                        style={{
                                                            width:
                                                                `${Math.min(
                                                                    Math.max(
                                                                        course.videoPercentage ||
                                                                        0,
                                                                        0
                                                                    ),
                                                                    100
                                                                )}%`,

                                                            height:
                                                                "100%",

                                                            background:
                                                                isSelected
                                                                    ? "#f5b45f"
                                                                    : "#555",

                                                            borderRadius:
                                                                "10px"
                                                        }}
                                                    />

                                                </div>

                                            </div>

                                        );

                                    }
                                )}

                        </div>

                    </div>

                </div>

            </div>

        </section>
    );
};

export default Learning;